import React, { useState, useCallback, useMemo } from 'react'
import { NoticeLoading } from '@fs/zion-icon'
import {
  Button,
  useOverlay,
  DialogOverlay,
  DialogOverlayFooter,
  DialogOverlayActions,
  DescriptionTooltip,
} from '@fs/zion-ui'
import { Trans } from 'react-i18next/icu.macro'
import { useIntlDateTime } from '../../../../hooks'
import useRestore from './useRestore'
import transl from '../../ChangeHistory.transl'

export default function RestoreButton({ onFinish, isCurrentVersion, restoreData, wasRemoved = false, date }) {
  const overlay = useOverlay()
  const [loading, setLoading] = useState(false)

  const formatDate = useIntlDateTime()
  const formattedDate = useMemo(() => {
    if (date == null) return transl.unknown()
    return formatDate({ date })
  }, [date, formatDate])

  const restoreSelectedChanges = useRestore({
    restoreData,
    setLoading,
    onFinish,
  })

  const onDialogConfirm = useCallback(async () => {
    await restoreSelectedChanges()
    overlay.close()
  }, [restoreSelectedChanges, overlay])

  return !wasRemoved ? (
    <>
      {isCurrentVersion ? (
        <DescriptionTooltip
          label={transl.currentVersion()}
          description={transl.currentVersionDescription()}
          target={
            <MakeButton loading={loading} onClick={overlay.handleClick} disabled>
              {transl.currentVersion()}
            </MakeButton>
          }
        />
      ) : (
        <MakeButton loading={loading} onClick={overlay.handleClick}>
          {transl.restore()}
        </MakeButton>
      )}
      <DialogOverlay {...overlay} title={transl.restoreDialogHeader()} size="md">
        <Trans
          i18nKey="ChangeHistory.restoreDialogContent"
          defaults="This will change the information to what it was on <br /> {date}"
          values={{ date: formattedDate }}
        />

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button onClick={overlay.close}>{transl.cancel()}</Button>
            <Button
              data-testid="dialog-restore-btn"
              emphasis="high"
              Icon={loading ? NoticeLoading : null}
              disabled={loading}
              onClick={() => onDialogConfirm()}
            >
              {transl.restore()}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    </>
  ) : null
}

function MakeButton({ loading, onClick, children, disabled = false }) {
  return (
    <Button
      emphasis="low"
      Icon={loading ? NoticeLoading : null}
      disabled={loading || disabled}
      onClick={onClick}
      data-testid="restore-button"
    >
      {children}
    </Button>
  )
}
